import React, { useRef, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Link2, Trash2, FileText, Image as ImageIcon } from "lucide-react";

const abs = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");

// Shared Upload-or-URL field. accept: "image" | "pdf". value is a URL string.
export const FileOrUrlField = ({ label, value, onChange, accept = "image", testid }) => {
  const [mode, setMode] = useState("upload");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const isImage = accept === "image";

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (isImage && !["image/png", "image/jpeg", "image/webp"].includes(file.type)) { toast.error("Only PNG, JPG or WebP images"); return; }
    if (!isImage && file.type !== "application/pdf") { toast.error("Only PDF files"); return; }
    if (isImage && file.size > 10 * 1024 * 1024) { toast.error("Image must be 10 MB or smaller"); return; }
    if (!isImage && file.size > 25 * 1024 * 1024) { toast.error("PDF must be 25 MB or smaller"); return; }
    setBusy(true);
    try {
      const res = isImage ? await adminApi.uploadFileImage(file) : await adminApi.uploadFileDocument(file);
      onChange(res.url); toast.success("Uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const keepLink = () => {
    if (!url.startsWith("https://")) { toast.error("URL must start with https://"); return; }
    onChange(url); setUrl(""); toast.success("Link saved");
  };
  const importUrl = async () => {
    if (!url.startsWith("https://")) { toast.error("URL must start with https://"); return; }
    setBusy(true);
    try { const res = await adminApi.importFileUrl(url, isImage ? "image" : "pdf"); onChange(res.url); setUrl(""); toast.success("Imported to storage"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not import that URL"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      {label && <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">{label}</p>}
      <div className="rounded-lg border border-gmoaa-border-subtle bg-white p-3">
        {value ? (
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded border border-gmoaa-border-subtle bg-gmoaa-surface">
              {isImage ? <img src={abs(value)} alt="" className="h-full w-full object-cover" data-testid={testid && `${testid}-preview`} /> : <FileText size={28} className="text-gmoaa-text-muted" />}
            </div>
            <a href={abs(value)} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-xs text-gmoaa-green hover:underline">{value}</a>
            <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={() => onChange("")} data-testid={testid && `${testid}-remove`}><Trash2 size={15} /></Button>
          </div>
        ) : (
          <div className="mb-3 flex items-center gap-2 text-xs text-gmoaa-text-muted">
            {isImage ? <ImageIcon size={14} /> : <FileText size={14} />} No {isImage ? "image" : "PDF"} yet
          </div>
        )}

        <div className="mb-2 flex gap-1">
          <button type="button" onClick={() => setMode("upload")} className={`rounded px-2.5 py-1 text-xs font-medium ${mode === "upload" ? "bg-gmoaa-green text-white" : "bg-gmoaa-surface text-gmoaa-text-dark"}`}><Upload size={12} className="mr-1 inline" />Upload</button>
          <button type="button" onClick={() => setMode("url")} className={`rounded px-2.5 py-1 text-xs font-medium ${mode === "url" ? "bg-gmoaa-green text-white" : "bg-gmoaa-surface text-gmoaa-text-dark"}`}><Link2 size={12} className="mr-1 inline" />Paste URL</button>
        </div>

        {mode === "upload" ? (
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gmoaa-green bg-white px-3 py-2 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light">
            <Upload size={14} /> {busy ? "Uploading…" : (value ? "Replace" : (isImage ? "Upload image" : "Upload PDF"))}
            <input ref={fileRef} type="file" accept={isImage ? "image/png,image/jpeg,image/webp" : "application/pdf"} className="hidden" onChange={onFile} disabled={busy} data-testid={testid && `${testid}-input`} />
          </label>
        ) : (
          <div className="space-y-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="h-8 text-sm" data-testid={testid && `${testid}-url`} />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={keepLink} disabled={busy} data-testid={testid && `${testid}-keep`}>Keep as link</Button>
              <Button size="sm" onClick={importUrl} disabled={busy} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover" data-testid={testid && `${testid}-import`}>{busy ? "…" : "Import to storage"}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileOrUrlField;
